import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import {
	ThemeBorderVariable,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
	ThemeFontVariable,
	Box,
	Stack,
	Text,
	Flex,
	Divider,
	Icon,
	Icons,
	Accordion,
	AccordionItem,
	AccordionPanel,
	Heading,
} from "@caspeco/casper-ui-library";
import { ICampaignDiscountValues } from "types/campaign";
import { Cart } from "types/cart";
import { Order } from "types/order";
import { useBaseColorValue } from "components/base-color-context/BaseColorContext";
import Price from "components/price/Price";
import { getSummary } from "./summary-helpers";

interface ISummary {
	data: Cart | Order;
	campaigns?: ICampaignDiscountValues[];
	headerText: string | JSX.Element;
	accordionOptions?: AccordionOptions;
}

type AccordionOptions = {
	/**
	 * If true, the summary will be shown in an accordion.
	 * If false, the summary will be shown in a box.
	 */
	useAccordion: boolean;
	expandedByDefault?: boolean;
	onChange?: (index: number | number[]) => void;
};

/**
 * Shows a summary of items to be purchased (if @param data is a Cart)
 * or items that have been purchased (if @param data is an Order).
 * The appearance is the same.
 */
export function Summary({ data, campaigns, headerText, accordionOptions }: ISummary) {
	const { baseColor } = useBaseColorValue();

	const [expandedIndex, setExpandedIndex] = useState<number | number[]>(
		accordionOptions?.expandedByDefault ? 0 : -1,
	);

	const onAccordionChange = (index: number | number[]) => {
		setExpandedIndex(index);
		accordionOptions?.onChange?.(index);
	};

	let textColor: ThemeColorVariable;
	let borderColor: ThemeColorVariable;
	switch (baseColor) {
		case "background":
			textColor = ThemeColorVariable.OnBackground;
			borderColor = ThemeColorVariable.OnBackgroundBorder;
			break;
		case "surface":
		default:
			textColor = ThemeColorVariable.OnSurface;
			borderColor = ThemeColorVariable.OnSurfaceBorder;
			break;
	}

	return accordionOptions?.useAccordion ? (
		<Box
			color={textColor}
			p={ThemeSpaceVariable.Medium}
			borderRadius={ThemeRadiusVariable.Medium}
			border={expandedIndex != -1 ? 0 : ThemeBorderVariable.XSmall}
			borderColor={borderColor}
			padding={0}
		>
			<Accordion variant="noBorder" index={expandedIndex} onChange={onAccordionChange}>
				<AccordionItem
					title={
						<Box textAlign="left" mt={ThemeSpaceVariable.Small} mb={ThemeSpaceVariable.Small} w="100%">
							{headerText}
						</Box>
					}
				>
					<AccordionPanel>
						<SummaryContent data={data} campaigns={campaigns} />
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</Box>
	) : (
		<>
			<Heading
				as="h3"
				fontSize={ThemeFontSizeVariable.Large}
				mb={ThemeSpaceVariable.Small}
				fontFamily={ThemeFontVariable.SubHeader}
				color={ThemeColorVariable.OnBackgroundHeader}
				fontWeight="medium"
			>
				{headerText}
			</Heading>
			<Box
				color={textColor}
				p={ThemeSpaceVariable.Medium}
				borderRadius={ThemeRadiusVariable.Small}
				border={ThemeBorderVariable.XSmall}
				borderColor={borderColor}
			>
				<SummaryContent data={data} campaigns={campaigns} />
			</Box>
		</>
	);
}

const SummaryContent = ({
	data,
	campaigns,
}: {
	data: Cart | Order;
	campaigns?: ICampaignDiscountValues[];
}) => {
	const { t } = useTranslation();
	const { baseColor } = useBaseColorValue();

	let borderColor: ThemeColorVariable;
	let detailColor: ThemeColorVariable;
	switch (baseColor) {
		case "background":
			borderColor = ThemeColorVariable.OnBackgroundBorder;
			detailColor = ThemeColorVariable.OnBackgroundSubdued;
			break;
		case "surface":
		default:
			borderColor = ThemeColorVariable.OnSurfaceBorder;
			detailColor = ThemeColorVariable.OnSurfaceSubdued;
			break;
	}

	const summary = getSummary(data);
	if (!summary) return null;

	const { tipAmount, totalPrice, totalPriceExcludingTips, items } = summary;
	const hasTip = tipAmount && tipAmount.cents > 0;

	return (
		<>
			<Stack spacing={ThemeSpaceVariable.XSmall}>
				{items.map((item) => {
					const allExtras = item.options.concat(item.extras);

					return (
						<Box key={item.id}>
							<Box display="flex" justifyContent="space-between">
								<Box display="flex" flexDirection="column">
									<Text>{`${item.quantity} x ${item.name}`}</Text>
									<Text
										fontSize={ThemeFontSizeVariable.Small}
										color={detailColor}
										ml={ThemeSpaceVariable.Small}
									>
										{allExtras.length > 0 && <>{allExtras.join(", ")}</>}
									</Text>
								</Box>
								<Price showCurrency={false} inHousePrice={item.price} forceDecimals hideIfZero={false} />
							</Box>
						</Box>
					);
				})}
				{campaigns &&
					campaigns.map((campaign, index) => (
						// Use index as key as campaigns are not unique, both names and ids can appear multiple times
						<Box as="article" key={index}>
							<Flex align="center" gap={ThemeSpaceVariable.Small}>
								<Icon color="inherit" icon={Icons.Discount} />
								<Flex justify="space-between" w="100%">
									<Text>{campaign.name}</Text>
									<Flex align="center">
										<Price
											prepend="-"
											inHousePrice={campaign.discountAmount}
											showCurrency={false}
											forceDecimals
										/>
									</Flex>
								</Flex>
							</Flex>
							{campaign.code && (
								<Text fontSize={ThemeFontSizeVariable.Small} color={detailColor}>
									{campaign.code}
								</Text>
							)}
						</Box>
					))}
			</Stack>
			<Divider my={ThemeSpaceVariable.Small} borderColor={borderColor} />
			<Stack spacing={ThemeSpaceVariable.XSmall}>
				{hasTip && (
					<>
						<Box display="flex" justifyContent="space-between">
							<Text>{t("money_subtotal")}</Text>
							<Price inHousePrice={totalPriceExcludingTips} showCurrency={false} forceDecimals />
						</Box>
						<Box display="flex" justifyContent="space-between">
							<Text>{t("money_tip")}</Text>
							<Price showCurrency={false} forceDecimals inHousePrice={tipAmount} />
						</Box>
					</>
				)}
				<Flex justifyContent="space-between">
					<Flex gap={ThemeSpaceVariable.XSmall}>
						<Text fontWeight="bold">{`${t("money_total")} ${totalPrice.currency}`}</Text>
						<Text color={detailColor}>{`(${t("money_including_vat")})`}</Text>
					</Flex>
					<Price
						fontWeight="bold"
						align="right"
						inHousePrice={totalPrice}
						showCurrency={false}
						forceDecimals
						hideIfZero={false}
					/>
				</Flex>
			</Stack>
		</>
	);
};
